import { Fragment, useState, type ReactNode } from 'react'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { useTranslation } from 'next-i18next'
import { MaintenanceBanner } from 'shared/components/maintenance-banner'
import {
  MobilePublicLanguageSwitcher,
  PublicDashboardLanguageSwitcher,
} from 'shared/components/public-dashboard-language-switcher'
import { featureFlagKeys } from 'shared/feature-flags/feature-keys'
import { useBrand } from 'modules/white-label/contexts/brand-context'
import ArrowLeftIcon from '../icons/arrow-left-icon'
import BurgerIcon from '../icons/burger-icon'
import { ScreenHeightDiv } from './screen-height-div'

interface Props {
  logoSrc?: string
}

function PublicDashboardHeader({ logoSrc }: Props): ReactNode {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const { brand } = useBrand()
  const isMaintenanceBannerEnabled = useFeatureIsOn(featureFlagKeys.enableMaintenanceBanner)

  const close = (): void => setIsOpen(false)

  return (
    <>
      <nav className="top-0 z-10 bg-background-header">
        <div className="z-10 mx-auto flex h-[69px] w-full items-center justify-between gap-5 px-4 text-background-secondary sm:px-6 md:px-14">
          <div className="flex items-center gap-5">
            <div className="rounded-full ring-background-secondary ring-offset-0 focus-visible:ring-2">
              <img
                className="max-h-[35px] max-w-[35px]"
                src={logoSrc || brand.logo?.path}
                alt={brand.name}
              />
            </div>
          </div>
          <div className={`flex w-full items-center justify-end md:hidden`}>
            <button aria-label="Open Menu" onClick={() => setIsOpen(true)}>
              <BurgerIcon className="size-8" />
            </button>
          </div>
          <div className={`hidden h-full items-center justify-around gap-4 md:flex`}>
            <PublicDashboardLanguageSwitcher />
          </div>
        </div>
        {isMaintenanceBannerEnabled && <MaintenanceBanner data-from="public-dashboard-header" />}
      </nav>
      <Transition show={isOpen} as={Fragment}>
        <Dialog className="fixed inset-0 z-10" onClose={close}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 size-full bg-black/50" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="ease-in duration-200"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <DialogPanel>
              <ScreenHeightDiv className="fixed top-0 right-0 z-10 flex min-w-68 flex-col overflow-auto bg-background-secondary px-4 py-5 text-text-primary">
                <div onClick={close} className="flex w-fit cursor-pointer items-center gap-2">
                  <ArrowLeftIcon className="stroke-text-primary" />
                  <span>{t('mobile_nav.back_to_page')}</span>
                </div>
                <div className="mt-auto flex flex-col gap-3">
                  <div className="flex items-center">
                    <div className="ml-auto">
                      <MobilePublicLanguageSwitcher />
                    </div>
                  </div>
                </div>
              </ScreenHeightDiv>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>
    </>
  )
}

export default PublicDashboardHeader
